import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { useBasePath } from '@/lib/paths'
import { TaxRegistrationSummary } from '@/types'
import { FileText, Plus } from 'lucide-react'

type Props = {
  registrations: TaxRegistrationSummary[]
  search: string
  pagination: PaginationMeta
}

export default function TaxRegistrationsIndex({ registrations, search, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title="Tax Registrations" breadcrumbs={[{ label: 'Tax Registrations' }]}>
      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Tax Registrations</h1>
          <p className="text-sm text-muted-foreground">Manage your tax registrations</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm">
              <Link href={`${basePath}/tax_registrations/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New Registration
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <SearchInput value={search} placeholder="Search registrations..." />
        </div>

        {registrations.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <FileText className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No tax registrations yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Apply for your first tax registration</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/tax_registrations/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Get Started
              </Link>
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg">
            {registrations.map((reg, i) => (
              <Link
                key={reg.id}
                href={`${basePath}/tax_registrations/${reg.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{reg.type_name}</span>
                      <StageBadge stage={reg.stage} variant="formation" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {reg.business_name} · {reg.jurisdiction_label || reg.type_code}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
